import { cliente } from '@/lib/cliente';
import type {
  ActualizarDetalleCasoSchema,
  BancoAsignado,
  CrearDetalleCasoSchema,
  FiltrosPagosCredito,
  PagoDetalleCasoSchema,
} from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';

export function useSemanas() {
  return useQuery({
    queryKey: ['semanas'],
    queryFn: async () => {
      const res = await cliente.semanas.listar();
      if (res.error) throw new Error(res.error.mensaje);
      return res.data ?? [];
    },
  });
}

export function useResumenSemana(semanaId: number | null) {
  return useQuery({
    queryKey: ['semana', semanaId],
    queryFn: async () => {
      if (!semanaId) return null;
      const res = await cliente.semanas.detalle(semanaId);
      if (res.error) throw new Error(res.error.mensaje);
      return res.data;
    },
    enabled: semanaId !== null,
  });
}

export function useActualizarPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number;
      pagado?: boolean;
      monedaPago?: 'C' | 'D' | null;
      montoAdeudado?: number | null;
      montoProgramado?: number | null;
      montoPagado?: number | null;
      notas?: string | null;
      /** Banco de este pago (null = vuelve al por defecto del concepto; 'MANUAL' = a mano). */
      banco?: BancoAsignado | null;
      /** Cuenta del proveedor elegida a mano (null = automática). */
      proveedorCuentaId?: number | null;
    }) => cliente.pagos.actualizar(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['semana'] });
      // El histórico de proveedores de crédito lee las mismas filas de flu.pagos.
      qc.invalidateQueries({ queryKey: ['pagos-credito'] });
    },
  });
}

/**
 * Histórico de pagos a proveedores de crédito: el detalle que en Pagos Semanales
 * se ve colapsado en dos líneas resumen (una por moneda).
 */
export function usePagosCredito(filtros: FiltrosPagosCredito) {
  return useQuery({
    queryKey: ['pagos-credito', filtros],
    queryFn: async () => {
      const res = await cliente.pagosCredito.listar(filtros);
      if (res.error) throw new Error(res.error.mensaje);
      return res.data ?? null;
    },
    placeholderData: (anterior) => anterior,
  });
}

/**
 * "Sugerir bancos": asigna BAC o BN a los pagos pendientes de la semana que todavía no tienen banco,
 * según el banco de la cuenta de su proveedor. No pisa lo que ya se decidió.
 */
export function useSugerirBancos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (semanaId: number) => cliente.semanas.sugerirBancos(semanaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['semana'] });
    },
  });
}

export function useCrearSemana() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: cliente.semanas.crear,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['semanas'] });
    },
  });
}

/** Abre la semana de hoy si todavía no existe (se pide al entrar a Pagos Semanales). */
export function useAsegurarSemanaActual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => cliente.semanas.asegurarActual(),
    onSuccess: (r) => {
      if (r.data?.creada) qc.invalidateQueries({ queryKey: ['semanas'] });
    },
  });
}

export function useTraerPendientes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (semanaId: number) => cliente.semanas.traerPendientes(semanaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['semana'] });
    },
  });
}

export function useSincronizarConceptos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (semanaId: number) => cliente.semanas.sincronizarConceptos(semanaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['semana'] });
    },
  });
}

export function useActualizarSaldos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number;
      tipoDeCambio?: number | null;
      quinta?: number | null;
      bancoColones?: number | null;
      bancoDolares?: number | null;
    }) => cliente.semanas.actualizarSaldos(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['semana'] });
      qc.invalidateQueries({ queryKey: ['semanas'] });
    },
  });
}

export function useProveedores() {
  return useQuery({
    queryKey: ['proveedores'],
    queryFn: async () => {
      const res = await cliente.proveedores.listar();
      if (res.error) throw new Error(res.error.mensaje);
      return res.data ?? [];
    },
  });
}

export function useCrearProveedor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: cliente.proveedores.crear,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proveedores'] }),
  });
}

export function useActualizarProveedor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: number } & Parameters<typeof cliente.proveedores.actualizar>[1]) =>
      cliente.proveedores.actualizar(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['proveedores'] }),
  });
}

export function useConceptos({ soloActivos = true }: { soloActivos?: boolean } = {}) {
  return useQuery({
    queryKey: ['conceptos', soloActivos],
    queryFn: async () => {
      const res = await cliente.conceptos.listar(soloActivos);
      if (res.error) throw new Error(res.error.mensaje);
      return res.data ?? [];
    },
  });
}

export function useObligaciones(params?: { conceptoId?: number; pendientes?: boolean }) {
  return useQuery({
    queryKey: ['obligaciones', params],
    queryFn: async () => {
      const res = await cliente.obligaciones.listar(params);
      if (res.error) throw new Error(res.error.mensaje);
      return res.data ?? [];
    },
  });
}

export function useCrearObligacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: cliente.obligaciones.crear,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['obligaciones'] }),
  });
}

export function useActualizarObligacion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: number } & Parameters<typeof cliente.obligaciones.actualizar>[1]) =>
      cliente.obligaciones.actualizar(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['obligaciones'] });
      qc.invalidateQueries({ queryKey: ['semana'] });
    },
  });
}

export function useProyeccion(semanas: 6 | 8, meses = 3) {
  return useQuery({
    queryKey: ['proyeccion', semanas, meses],
    queryFn: async () => {
      const res = await cliente.proyeccion.obtener({ semanas, meses });
      if (res.error) throw new Error(res.error.mensaje);
      return res.data ?? null;
    },
    placeholderData: (anterior) => anterior,
  });
}

export function useSugerenciasProveedor() {
  return useQuery({
    queryKey: ['conceptos-sugerencias-proveedor'],
    queryFn: async () => {
      const res = await cliente.conceptos.sugerenciasProveedor();
      if (res.error) throw new Error(res.error.mensaje);
      return res.data ?? [];
    },
  });
}

export function useVincularProveedorConcepto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: { id: number } & Parameters<typeof cliente.conceptos.vincularProveedor>[1]) =>
      cliente.conceptos.vincularProveedor(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conceptos-sugerencias-proveedor'] });
      qc.invalidateQueries({ queryKey: ['conceptos'] });
    },
  });
}

export function useCategorias() {
  return useQuery({
    queryKey: ['categorias'],
    queryFn: async () => {
      const res = await cliente.categorias.listar();
      if (res.error) throw new Error(res.error.mensaje);
      return res.data ?? [];
    },
  });
}

export function useDetalleSemana(semanaId: number | null, categoriaId: number | null) {
  return useQuery({
    queryKey: ['detalle-semana', semanaId, categoriaId],
    queryFn: async () => {
      if (!semanaId || !categoriaId) return null;
      const res = await cliente.detalleSemana.listar(semanaId, categoriaId);
      if (res.error) throw new Error(res.error.mensaje);
      return res.data;
    },
    enabled: semanaId !== null && categoriaId !== null,
  });
}

/** Guardar un renglón (crear o actualizar) o eliminarlo; refresca el detalle y la hoja semanal. */
export function useCambiarDetalleSemana() {
  const qc = useQueryClient();
  const refrescar = () => {
    qc.invalidateQueries({ queryKey: ['detalle-semana'] });
    qc.invalidateQueries({ queryKey: ['semana'] });
  };
  return {
    crear: useMutation({
      mutationFn: (d: z.infer<typeof CrearDetalleCasoSchema>) => cliente.detalleSemana.crear(d),
      onSuccess: refrescar,
    }),
    actualizar: useMutation({
      mutationFn: (a: { id: number; datos: z.infer<typeof ActualizarDetalleCasoSchema> }) =>
        cliente.detalleSemana.actualizar(a.id, a.datos),
      onSuccess: refrescar,
    }),
    eliminar: useMutation({
      mutationFn: (id: number) => cliente.detalleSemana.eliminar(id),
      onSuccess: refrescar,
    }),
    guardarPago: useMutation({
      mutationFn: (a: { id: number; datos: z.infer<typeof PagoDetalleCasoSchema> }) =>
        cliente.detalleSemana.guardarPago(a.id, a.datos),
      onSuccess: refrescar,
    }),
  };
}

/** Beneficiarios registrados (colaboradores y clientes a quienes se les ha armado un caso). */
export function useBeneficiarios() {
  return useQuery({
    queryKey: ['beneficiarios'],
    queryFn: async () => {
      const res = await cliente.beneficiarios.listar();
      if (res.error) throw new Error(res.error.mensaje);
      return res.data ?? [];
    },
  });
}

/** Busca en dbo.Colaborador por nombre o cédula; hasta que se escriben 2 letras no consulta nada. */
export function useBuscarColaboradores(texto: string, categoriaId?: number | null) {
  return useQuery({
    queryKey: ['colaboradores', texto, categoriaId ?? null],
    queryFn: async () => {
      const res = await cliente.beneficiarios.colaboradores(texto, categoriaId);
      if (res.error) throw new Error(res.error.mensaje);
      return res.data ?? [];
    },
    enabled: texto.trim().length >= 2,
  });
}
